import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router'
import { Button } from '#/components/ui/button'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '#/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '#/components/ui/form'
import { Input } from '#/components/ui/input'
import { chargerAPI } from '#/api/charger'
import { AlertBox } from '#/components/ui/alert-box'
import { mutationError } from '#/lib/mutation-error'
import { toast } from 'sonner'
import { useNavigate } from '#/hooks/use-navigate'


interface FormInputs {
  serialNumber: string
}

interface DeleteChargerModalProps {
  serialNumber: string
  onClose: () => void
}

export function DeleteChargerModal({ serialNumber, onClose }: DeleteChargerModalProps) {
  const { t } = useTranslation([ 'chargers' ])
  const { organization } = useParams()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const form = useForm<FormInputs>({
    defaultValues: {
      serialNumber: ''
    }
  })

  const removeChargerMutation = useMutation({
    mutationFn: () => chargerAPI.delete(serialNumber),
    onSuccess: () => {
      // Invalidate the specific charger and the charger list
      queryClient.invalidateQueries({ queryKey: [ 'organizations', organization, 'chargers' ] })
      queryClient.removeQueries({
        queryKey: [ 'organizations', organization, 'chargers', serialNumber ]
      })
      toast.success(t('charger_list.menu.toasts.remove_success'))
      onClose()
      navigate('..')
    },
    onError: () => {
      // handled via AlertBox
    }
  })

  const handleSubmit = form.handleSubmit(values => {
    if ((values.serialNumber ?? '').trim() !== serialNumber.trim()) {
      form.setError('serialNumber', {
        type: 'manual',
        message: t('charger_list.menu.toasts.remove_mismatch')
      })

      return
    }

    removeChargerMutation.mutate()
  })

  return (
    <Dialog open modal onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('charger_list.menu.remove_title', { serialNumber })}</DialogTitle>
          <DialogDescription>{t('charger_list.menu.remove_description')}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {removeChargerMutation.error && (
              <AlertBox
                error
                title={mutationError(removeChargerMutation.error, {
                  default: t('charger_list.menu.toasts.remove_error')
                })}
              />
            )}

            <FormField
              name="serialNumber"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('charger_list.menu.remove_field')}</FormLabel>
                  <FormControl>
                    <Input
                      required
                      maxLength={128}
                      autoComplete="off"
                      autoFocus
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="gap-2 justify-start">
              <Button type="button" variant="outline" onClick={onClose}>
                {t('charger_list.menu.remove_cancel')}
              </Button>
              <Button
                type="submit"
                variant="destructive"
                disabled={removeChargerMutation.isPending}
              >
                {t('charger_list.menu.remove_submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
